import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, DollarSign, RefreshCw, TrendingUp, Send, Eye, Megaphone, ArrowUpDown } from 'lucide-react';
import { cartsApi } from '../../api/endpoints/carts';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { StatCard } from '../../components/ui/StatCard';
import { SearchInput } from '../../components/ui/SearchInput';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { Input } from '../../components/ui/Input';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Alert';
import { EmailTemplateEditor } from '../../components/email-editor';
import type { EmailEditorState } from '../../components/email-editor';
import { usePagination } from '../../hooks/usePagination';
import { useDebounce } from '../../hooks/useDebounce';
import { formatRelativeTime, formatCurrency, getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { AbandonedCart } from '../../types/cart';
import { ApiError } from '../../types/api';

// Channels the backend can actually deliver on. WhatsApp stays visible so the
// roadmap is obvious, but it is gated instead of silently sending nothing.
const RETARGET_CHANNELS = ['email'];

function FunnelStat({ label, value, sub, hint }: { label: string; value: number; sub?: string; hint?: string }) {
  return (
    <div title={hint}>
      <p className="text-xs text-text-muted">{label}</p>
      <p className="mt-0.5 text-xl font-bold">{value}</p>
      {sub && <p className="text-xs text-text-muted">{sub}</p>}
    </div>
  );
}

export function CartList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { page, limit, goToPage, reset } = usePagination();

  // Search state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // Sort & date filter state
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Items modal state
  const [viewingCart, setViewingCart] = useState<AbandonedCart | null>(null);

  // Recovery modal state
  const [recoveryCart, setRecoveryCart] = useState<AbandonedCart | null>(null);

  // Bulk retarget modal
  const [showRetarget, setShowRetarget] = useState(false);
  const [retargetPlatform, setRetargetPlatform] = useState('email');
  const [retargetMessage, setRetargetMessage] = useState('');
  const [retargetMinCart, setRetargetMinCart] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['carts', storeId, { page, limit, search: debouncedSearch, sort: sortDir, startDate, endDate }],
    queryFn: () => cartsApi.list(storeId!, {
      page,
      limit,
      search: debouncedSearch || undefined,
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  const recoveryMutation = useMutation({
    mutationFn: (data: { cartId: string; state: EmailEditorState }) =>
      cartsApi.sendRecovery(storeId!, data.cartId, {
        channel: 'email',
        message: data.state.bodyHtml,
        template: data.state.templateType,
        subject: data.state.subject,
        colors: data.state.colors as unknown as Record<string, string>,
        greeting: data.state.greeting,
        ctaText: data.state.ctaText,
        ctaUrl: data.state.ctaUrl,
      }),
    onSuccess: () => {
      toast('success', t('cartRecovery.sendSuccess'));
      setRecoveryCart(null);
      queryClient.invalidateQueries({ queryKey: ['carts', storeId] });
    },
    onError: () => {
      toast('error', t('common.error'));
    },
  });

  const retargetCountQuery = useQuery({
    queryKey: ['retarget-count', storeId, retargetPlatform, retargetMinCart],
    queryFn: () => cartsApi.getRetargetCount(storeId!, retargetPlatform, retargetMinCart > 0 ? { minCartValue: retargetMinCart } : undefined),
    enabled: !!storeId && showRetarget,
  });

  const retargetStats = retargetCountQuery.data;
  const channelAvailable = RETARGET_CHANNELS.includes(retargetPlatform);
  const eligibleCount = channelAvailable ? retargetStats?.count : 0;

  const bulkRetargetMutation = useMutation({
    mutationFn: () => cartsApi.bulkRetarget(storeId!, {
      platform: retargetPlatform,
      message: retargetMessage || `${t('cartRecovery.defaultRetargetMsg', 'Hi {name}, you left items in your cart at {storeName}. Complete your order now!')}`,
      filters: retargetMinCart > 0 ? { minCartValue: retargetMinCart } : undefined,
    }),
    onSuccess: (result) => {
      // The run is queued, not finished: the backend delivers in the background
      // because a few hundred sends take far longer than the request timeout.
      if (result.queued === 0) {
        toast('info', t('cartRecovery.retargetNothingToSend', 'No new customers to contact right now'));
      } else if (result.skipped > 0) {
        toast('success', t('cartRecovery.retargetQueuedWithSkips', 'Sending to {{queued}} customers. {{skipped}} skipped (already contacted or unreachable).', { queued: result.queued, skipped: result.skipped }));
      } else {
        toast('success', t('cartRecovery.retargetQueued', 'Sending to {{queued}} customers. They will receive the message shortly.', { queued: result.queued }));
      }
      setShowRetarget(false);
      queryClient.invalidateQueries({ queryKey: ['carts', storeId] });
      queryClient.invalidateQueries({ queryKey: ['retarget-count', storeId] });
    },
    onError: (error) => {
      // 409 means a previous run is still delivering. Telling the merchant that
      // is what stops them from hammering the button and re-sending.
      if (error instanceof ApiError && error.code === 'CONFLICT') {
        toast('error', t('cartRecovery.retargetInProgress', 'A retargeting run is already in progress. Wait for it to finish.'));
        return;
      }
      if (error instanceof ApiError && error.code === 'UNPROCESSABLE') {
        toast('error', t('cartRecovery.channelUnavailable', 'This channel is not available yet'));
        return;
      }
      toast('error', t('common.error'));
    },
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    reset();
  };

  const columns: Column<AbandonedCart>[] = [
    {
      key: 'customer',
      header: t('orders.customer'),
      render: (c) => (
        <div>
          <p className="text-sm font-medium">{c.customer?.name ?? '-'}</p>
          {c.customer?.email && <p className="text-xs text-text-muted">{c.customer.email}</p>}
        </div>
      ),
    },
    {
      key: 'items',
      header: t('orders.items'),
      render: (c) => (
        <div className="flex items-center gap-2">
          <span>{c.items.length}</span>
          <span className="text-xs text-text-muted">
            ({c.items.map((i) => i.title).join(', ').slice(0, 40)})
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewingCart(c);
            }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-accent hover:bg-accent/10 transition-colors"
          >
            <Eye className="h-3 w-3" />
            {t('cartRecovery.viewItems')}
          </button>
        </div>
      ),
    },
    {
      key: 'total',
      header: t('common.total'),
      sortable: true,
      render: (c) => (
        <CurrencyDisplay
          amount={c.total}
          currency={c.currency || currentStore?.currency}
          className="font-medium"
        />
      ),
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      render: (c) => (
        <span className="text-xs text-text-secondary">{formatRelativeTime(c.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (c) => {
        const hasContact = c.customer?.email || c.customer?.phone;
        return (
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasContact}
            onClick={(e) => {
              e.stopPropagation();
              setRecoveryCart(c);
            }}
          >
            <Send className="h-3.5 w-3.5" />
            {t('cartRecovery.sendRecovery')}
          </Button>
        );
      },
    },
  ];

  const carts = data?.carts ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;
  const stats = data?.stats;
  const funnel = stats?.retargeting;

  const currency = currentStore?.currency ?? 'SAR';

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.carts')}</h1>
        <Button onClick={() => setShowRetarget(true)} variant="secondary">
          <Megaphone className="h-4 w-4" />
          {t('cartRecovery.bulkRetarget', 'Bulk Retarget')}
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          title={t('cartRecovery.totalCarts')}
          value={stats?.totalCarts ?? 0}
          icon={<ShoppingCart className="h-5 w-5" />}
        />
        <StatCard
          title={t('cartRecovery.totalValue')}
          value={stats?.totalValue ?? 0}
          currency={currency}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <StatCard
          title={t('cartRecovery.recoveredCount')}
          value={stats?.recoveredCount ?? 0}
          icon={<RefreshCw className="h-5 w-5" />}
        />
        <StatCard
          title={t('cartRecovery.recoveryRate')}
          value={`${stats?.recoveryRate ?? 0}%`}
          icon={<TrendingUp className="h-5 w-5" />}
        />
      </div>

      {/* Retargeting funnel. Only shown once something has actually been sent,
          so a store that has never run a campaign sees four zeros less. */}
      {!!funnel?.sent && (
        <div className="mb-6 rounded-xl border border-border-default bg-bg-secondary p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">{t('cartRecovery.funnelTitle', 'Retargeting performance')}</h2>
            <p className="text-xs text-text-muted">
              {t('cartRecovery.funnelAttribution', 'Orders within {{days}} days of a message', { days: funnel.attributionDays })}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <FunnelStat label={t('cartRecovery.funnelSent', 'Sent')} value={funnel.sent} />
            <FunnelStat
              label={t('cartRecovery.funnelOpened', 'Opened')}
              value={funnel.opened}
              sub={`${funnel.openRate}%`}
              // Providers that prefetch images register an open nobody made.
              hint={t('cartRecovery.funnelOpenHint', 'Approximate: some mail apps load images automatically')}
            />
            <FunnelStat label={t('cartRecovery.funnelClicked', 'Clicked')} value={funnel.clicked} sub={`${funnel.clickRate}%`} />
            <FunnelStat
              label={t('cartRecovery.funnelConverted', 'Orders')}
              value={funnel.converted}
              sub={formatCurrency(funnel.revenue, currency)}
            />
          </div>
        </div>
      )}

      {/* Search & sort */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder={t('common.search')}
          className="max-w-sm"
        />
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'Newest first') },
            { value: 'asc', label: t('common.oldestFirst', 'Oldest first') },
          ]}
          value={sortDir}
          onChange={(v) => { setSortDir(v as 'desc' | 'asc'); reset(); }}
        />
      </div>
      {/* Date range filters */}
      <DateFilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={(v) => { setStartDate(v); reset(); }}
        onEndDateChange={(v) => { setEndDate(v); reset(); }}
        className="mb-4"
      />

      {/* Table */}
      <div className="rounded-xl border border-border-default bg-bg-secondary">
        <DataTable
          columns={columns}
          data={carts}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('common.noResults')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* View Items Modal */}
      <Modal
        isOpen={!!viewingCart}
        onClose={() => setViewingCart(null)}
        title={t('cartRecovery.cartItems')}
        size="lg"
      >
        {viewingCart && (
          <div className="space-y-3">
            {viewingCart.items.map((item, idx) => (
              <div
                key={`${item.productId}-${idx}`}
                className="flex items-center gap-4 rounded-lg border border-border-default bg-bg-primary p-3"
              >
                {item.image ? (
                  <img
                    src={getCdnUrl(item.image, viewingCart.storeId)}
                    alt={item.title}
                    className="h-14 w-14 rounded-lg object-cover border border-border-default"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-bg-tertiary text-text-muted">
                    <ShoppingCart className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.title}</p>
                  <p className="text-xs text-text-muted">
                    {formatCurrency(item.price, viewingCart.currency || currency)} x {item.quantity}
                  </p>
                </div>
                <p className="text-sm font-semibold whitespace-nowrap">
                  {formatCurrency(item.price * item.quantity, viewingCart.currency || currency)}
                </p>
              </div>
            ))}
            <div className="flex justify-end border-t border-border-default pt-3">
              <p className="text-base font-bold">
                {t('common.total')}: {formatCurrency(viewingCart.total, viewingCart.currency || currency)}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Retarget Modal */}
      <Modal
        isOpen={showRetarget}
        onClose={() => setShowRetarget(false)}
        title={t('cartRecovery.bulkRetarget', 'Bulk Retarget')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowRetarget(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => bulkRetargetMutation.mutate()}
              isLoading={bulkRetargetMutation.isPending}
              disabled={!channelAvailable || retargetCountQuery.isLoading || !eligibleCount}
            >
              <Send className="h-4 w-4" />
              {t('cartRecovery.sendToAll', 'Send to All')} ({retargetCountQuery.isLoading ? '...' : (eligibleCount ?? 0)})
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label={t('cartRecovery.recoveryChannel')}
            options={[
              { value: 'email', label: t('cartRecovery.email') },
              { value: 'whatsapp', label: `${t('cartRecovery.whatsapp')} (${t('common.comingSoon', 'Coming soon')})` },
            ]}
            value={retargetPlatform}
            onChange={(e) => setRetargetPlatform(e.target.value)}
          />
          {!channelAvailable && (
            <p className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
              {t('cartRecovery.channelUnavailable', 'This channel is not available yet')}
            </p>
          )}
          <Textarea
            id="retarget-msg"
            label={t('cartRecovery.recoveryMessage')}
            rows={4}
            value={retargetMessage}
            onChange={(e) => setRetargetMessage(e.target.value)}
            placeholder={t('cartRecovery.variablesHint')}
          />
          <Input
            label={t('cartRecovery.minCartFilter', 'Min Cart Value (optional)')}
            type="number"
            min="0"
            step="0.01"
            value={retargetMinCart || ''}
            onChange={(e) => setRetargetMinCart(Number(e.target.value) || 0)}
          />
          <div className="rounded-lg bg-bg-tertiary p-3 text-center">
            <p className="text-sm text-text-muted">{t('cartRecovery.matchingCustomers', 'Matching customers')}</p>
            <p className="text-2xl font-bold mt-1">{retargetCountQuery.isLoading ? '-' : (eligibleCount ?? 0)}</p>
            {/* The headline number is who will actually be mailed, so the
                exclusions have to be visible or it looks like carts vanished. */}
            {channelAvailable && retargetStats && (retargetStats.onCooldown > 0 || retargetStats.unreachable > 0 || retargetStats.unsubscribed > 0) && (
              <div className="mt-2 space-y-0.5 text-xs text-text-muted">
                {retargetStats.onCooldown > 0 && (
                  <p>{t('cartRecovery.skippedCooldown', '{{n}} contacted in the last {{hours}}h', { n: retargetStats.onCooldown, hours: retargetStats.cooldownHours ?? 24 })}</p>
                )}
                {retargetStats.unreachable > 0 && (
                  <p>{t('cartRecovery.skippedUnreachable', '{{n}} without an email address', { n: retargetStats.unreachable })}</p>
                )}
                {retargetStats.unsubscribed > 0 && (
                  <p>{t('cartRecovery.skippedUnsubscribed', '{{n}} unsubscribed', { n: retargetStats.unsubscribed })}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Send Recovery Modal - Email Template Editor */}
      <Modal
        isOpen={!!recoveryCart}
        onClose={() => {
          if (!recoveryMutation.isPending) setRecoveryCart(null);
        }}
        title={t('cartRecovery.sendRecovery')}
        size="2xl"
      >
        {recoveryCart && (
          <EmailTemplateEditor
            initialState={{ templateType: 'cart-recovery' }}
            context={{
              storeName: currentStore?.name || '',
              storeLogo: currentStore?.logo ? `https://00pays.com/stores/logos/${currentStore.logo}` : undefined,
              storeUrl: currentStore?.domain ? `https://${currentStore.domain}` : undefined,
              customerName: recoveryCart.customer?.name,
              customerEmail: recoveryCart.customer?.email,
              items: recoveryCart.items,
              total: recoveryCart.total,
              currency: recoveryCart.currency || currency,
            }}
            allowedTemplates={['cart-recovery', 'generic']}
            onSubmit={(state) => {
              recoveryMutation.mutate({ cartId: recoveryCart._id, state });
            }}
            onCancel={() => setRecoveryCart(null)}
            isSubmitting={recoveryMutation.isPending}
          />
        )}
      </Modal>
    </div>
  );
}
